import type { Tone } from "../../../components/ui";
import type { PricingMode, TripStatus, TrustLevel, UnitType, When } from "./api";

// ===========================================================================
// Cargo Market — etiquetas y formatos compartidos por embarcador y transportista.
// ===========================================================================

export const UNIT_LABELS: Record<UnitType, string> = {
  caja_53: "Caja seca 53'",
  sencillo: "Sencillo (tracto + caja 48')",
  full: "Full (doble remolque)",
  thermo: "Thermo (refrigerado)",
  plataforma: "Plataforma",
  pipa: "Pipa",
  tolva: "Tolva",
  torton: "Tórton",
  rabon: "Rabón",
  camioneta_35: "Camioneta 3.5 t",
};
export const UNIT_ICONS: Record<UnitType, string> = {
  caja_53: "ri-truck-line",
  sencillo: "ri-truck-line",
  full: "ri-truck-line",
  thermo: "ri-temp-cold-line",
  plataforma: "ri-stack-line",
  pipa: "ri-drop-line",
  tolva: "ri-inbox-archive-line",
  torton: "ri-truck-line",
  rabon: "ri-truck-line",
  camioneta_35: "ri-car-line",
};
export const UNIT_TYPE_OPTIONS = (Object.keys(UNIT_LABELS) as UnitType[]).map((value) => ({ value, label: UNIT_LABELS[value] }));

export const TRUST_META: Record<TrustLevel, { label: string; tone: Tone; icon: string; hint: string }> = {
  registrado: { label: "Registrado", tone: "neutral", icon: "ri-user-line", hint: "Datos básicos; aún no puede operar" },
  verificado: { label: "Verificado", tone: "info", icon: "ri-shield-check-line", hint: "Empresa, unidad y operador validados" },
  rastreado: { label: "Rastreado", tone: "success", icon: "ri-map-pin-user-line", hint: "Verificado + GPS integrado a WAYAK" },
  premium: { label: "Premium", tone: "primary", icon: "ri-vip-crown-line", hint: "Certificaciones, +20 viajes y 4.5★ o más" },
};
export const TRUST_ORDER: TrustLevel[] = ["registrado", "verificado", "rastreado", "premium"];

export const PRICING_LABELS: Record<PricingMode, string> = {
  fixed: "Precio fijo",
  offers: "Recibe ofertas",
  suggested: "Tarifa sugerida",
};

export const TRIP_STEPS: TripStatus[] = ["confirmed", "to_pickup", "at_pickup", "loaded", "in_transit", "at_dropoff", "delivered", "closed"];
export const TRIP_LABELS: Record<TripStatus, string> = {
  awarded: "Asignar unidad",
  confirmed: "Confirmado",
  to_pickup: "En camino a cargar",
  at_pickup: "En sitio de carga",
  loaded: "Cargado",
  in_transit: "En tránsito",
  at_dropoff: "En sitio de descarga",
  delivered: "Entregado",
  closed: "Cerrado",
  cancelled: "Cancelado",
};
export const TRIP_ICONS: Record<TripStatus, string> = {
  awarded: "ri-user-add-line",
  confirmed: "ri-checkbox-circle-line",
  to_pickup: "ri-navigation-line",
  at_pickup: "ri-map-pin-2-line",
  loaded: "ri-inbox-archive-line",
  in_transit: "ri-truck-line",
  at_dropoff: "ri-map-pin-range-line",
  delivered: "ri-hand-heart-line",
  closed: "ri-flag-2-line",
  cancelled: "ri-close-circle-line",
};
export function tripTone(status: TripStatus): Tone {
  if (status === "awarded") return "warning";
  if (status === "cancelled") return "error";
  if (status === "closed") return "neutral";
  if (status === "delivered") return "success";
  return "info";
}
/** Siguiente paso del flujo y quién lo da (para los botones de acción). */
export function nextStep(status: TripStatus): { status: TripStatus; actor: "carrier" | "shipper" } | null {
  const i = TRIP_STEPS.indexOf(status);
  if (status === "awarded" || i < 0 || i >= TRIP_STEPS.length - 1) return null;
  const next = TRIP_STEPS[i + 1];
  return { status: next, actor: next === "closed" ? "shipper" : "carrier" };
}

export const INCIDENT_LABELS: Record<string, string> = {
  delay: "Retraso",
  shortage: "Faltante",
  damage: "Daño",
  deviation: "Desvío de ruta",
  unauthorized_stop: "Parada no autorizada",
  panic: "Botón de pánico",
  theft: "Robo",
  seal_broken: "Sello no íntegro",
  other: "Otra",
};

export const PAYMENT_LABELS: Record<string, { label: string; tone: Tone }> = {
  escrow_pending: { label: "Pago por depositar", tone: "warning" },
  held: { label: "Pago retenido (escrow)", tone: "info" },
  released: { label: "Pago liberado", tone: "success" },
  on_credit: { label: "A crédito", tone: "info" },
  invoiced: { label: "Facturado a crédito", tone: "success" },
  refunded: { label: "Reembolsado", tone: "neutral" },
};

const mxn = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
export const money = (n: number | null | undefined) => (typeof n === "number" && Number.isFinite(n) ? mxn.format(n) : "—");
export const pct = (n: number) => `${Math.round(n * 1000) / 10}%`;

export function toDate(v: When): Date | null {
  if (!v) return null;
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = v._seconds ?? v.seconds;
  return typeof s === "number" ? new Date(s * 1000) : null;
}
const dayFmt = new Intl.DateTimeFormat("es-MX", { weekday: "short", day: "numeric", month: "short", timeZone: "America/Mexico_City" });
const timeFmt = new Intl.DateTimeFormat("es-MX", { hour: "2-digit", minute: "2-digit", timeZone: "America/Mexico_City" });
export const fmtDay = (v: When) => {
  const d = toDate(v);
  return d ? dayFmt.format(d) : "—";
};
export const fmtTime = (v: When) => {
  const d = toDate(v);
  return d ? timeFmt.format(d) : "—";
};
export const fmtDateTime = (v: When) => {
  const d = toDate(v);
  return d ? `${dayFmt.format(d)} · ${timeFmt.format(d)}` : "—";
};
/** «mar 22 oct · 08:00–12:00» */
export const fmtWindow = (w: { from: string; to: string } | null | undefined) => {
  if (!w) return "—";
  const a = toDate(w.from);
  const b = toDate(w.to);
  if (!a) return "—";
  return b && b.getTime() !== a.getTime() ? `${dayFmt.format(a)} · ${timeFmt.format(a)}–${timeFmt.format(b)}` : `${dayFmt.format(a)} · ${timeFmt.format(a)}`;
};
export function relative(v: When): string {
  const d = toDate(v);
  if (!d) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (Math.abs(mins) < 1) return "ahora";
  if (mins > 0 && mins < 60) return `hace ${mins} min`;
  if (mins > 0 && mins < 1440) return `hace ${Math.round(mins / 60)} h`;
  if (mins > 0) return `hace ${Math.round(mins / 1440)} d`;
  const ahead = -mins;
  if (ahead < 60) return `en ${ahead} min`;
  if (ahead < 1440) return `en ${Math.round(ahead / 60)} h`;
  return `en ${Math.round(ahead / 1440)} d`;
}

/** Valor para <input type="datetime-local"> en hora de México. */
export function toLocalInput(d: Date): string {
  const mx = new Date(d.getTime() - 6 * 3600 * 1000);
  return mx.toISOString().slice(0, 16);
}
/** Lee un <input type="datetime-local"> capturado en hora de México. */
export const fromLocalInput = (v: string) => (v ? new Date(`${v}:00-06:00`).toISOString() : "");

/** Catálogo de ciudades (mismo que el backend: services/cargoExchangeCities). */
export const CITIES: { key: string; city: string; state: string }[] = [
  ["cdmx", "Ciudad de México", "CDMX"], ["tlalnepantla", "Tlalnepantla", "MEX"], ["cuautitlan", "Cuautitlán Izcalli", "MEX"], ["toluca", "Toluca", "MEX"],
  ["queretaro", "Querétaro", "QRO"], ["san-juan-del-rio", "San Juan del Río", "QRO"], ["celaya", "Celaya", "GTO"], ["irapuato", "Irapuato", "GTO"],
  ["silao", "Silao", "GTO"], ["leon", "León", "GTO"], ["aguascalientes", "Aguascalientes", "AGS"], ["san-luis-potosi", "San Luis Potosí", "SLP"],
  ["guadalajara", "Guadalajara", "JAL"], ["monterrey", "Monterrey", "NL"], ["apodaca", "Apodaca", "NL"], ["saltillo", "Saltillo", "COAH"],
  ["nuevo-laredo", "Nuevo Laredo", "TAMPS"], ["reynosa", "Reynosa", "TAMPS"], ["altamira", "Altamira", "TAMPS"], ["puebla", "Puebla", "PUE"],
  ["tlaxcala", "Tlaxcala", "TLAX"], ["veracruz", "Veracruz", "VER"], ["tula", "Tula", "HGO"], ["pachuca", "Pachuca", "HGO"],
  ["morelia", "Morelia", "MICH"], ["lazaro-cardenas", "Lázaro Cárdenas", "MICH"], ["manzanillo", "Manzanillo", "COL"], ["chihuahua", "Chihuahua", "CHIH"],
  ["ciudad-juarez", "Ciudad Juárez", "CHIH"], ["hermosillo", "Hermosillo", "SON"], ["culiacan", "Culiacán", "SIN"], ["tijuana", "Tijuana", "BC"],
  ["merida", "Mérida", "YUC"], ["villahermosa", "Villahermosa", "TAB"], ["oaxaca", "Oaxaca", "OAX"], ["cuernavaca", "Cuernavaca", "MOR"],
  ["torreon", "Torreón", "COAH"], ["zacatecas", "Zacatecas", "ZAC"], ["durango", "Durango", "DGO"],
].map(([key, city, state]) => ({ key, city, state }));
export const CITY_OPTIONS = CITIES.map((c) => ({ value: c.key, label: `${c.city}, ${c.state}` }));
